/**
 * Identity Verification Modal
 * ===========================
 * Multi-step verification flow for NearU Card activation.
 */

import React, { useState, useEffect } from 'react';
import { X, Shield, Lock, CheckCircle, AlertCircle, Loader2, FileText, User, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IdentityVerificationModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('intro'); // intro, consent, verifying, success, error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consents, setConsents] = useState({
    privacy_policy: false,
    identity_notice: false,
    cardholder_agreement: false
  });

  useEffect(() => {
    if (isOpen) {
      setStep('intro');
      setError(null);
      setConsents({
        privacy_policy: false,
        identity_notice: false,
        cardholder_agreement: false
      });
    }
  }, [isOpen]);

  const handleContinue = () => {
    if (step === 'intro') {
      setStep('consent');
    }
  };

  const allConsentsGiven = Object.values(consents).every(v => v);

  const handleStartVerification = async () => {
    if (!allConsentsGiven) {
      setError('Please accept all required agreements.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/card/verification/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_acknowledged: true,
          privacy_policy_accepted: consents.privacy_policy,
          identity_notice_accepted: consents.identity_notice,
          cardholder_agreement_accepted: consents.cardholder_agreement
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to start verification');
      }

      // In sandbox mode, simulate verification completion
      setStep('verifying');
      
      // Simulate verification process
      setTimeout(async () => {
        try {
          const completeRes = await fetch(`${API}/card/verification/complete`, {
            method: 'POST'
          });

          if (completeRes.ok) {
            setStep('success');
            setTimeout(() => {
              onSuccess?.();
              onClose();
            }, 2000);
          } else {
            throw new Error('Verification failed');
          }
        } catch (err) {
          setStep('error');
          setError('Verification could not be completed. Please try again.');
        }
      }, 3000);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      backdropFilter: 'blur(10px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div 
        style={{
          background: 'linear-gradient(145deg, #1A0F0A 0%, #2C1B13 100%)',
          border: '1px solid rgba(165, 124, 51, 0.4)',
          borderRadius: '20px',
          maxWidth: '440px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        data-testid="verification-modal"
      >
        {/* Close Button */}
        {step !== 'verifying' && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(138, 115, 85, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#8A7355',
              zIndex: 10
            }}
          >
            <X size={20} />
          </button>
        )}

        {/* Intro Step */}
        {step === 'intro' && (
          <div style={{ padding: '32px 24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                border: '2px solid rgba(215, 181, 109, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <Shield size={40} color="#D7B56D" />
              </div>
              
              <h2 style={{
                fontSize: '24px',
                fontFamily: 'Cinzel, serif',
                background: 'linear-gradient(135deg, #D7B56D 0%, #E6D7C3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '12px'
              }}>
                Let's verify it's you
              </h2>
              
              <p style={{
                color: '#A57C33',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                To help protect NearU members and enable your NearU Card, identity verification is required.
              </p>
            </div>

            {/* Info Points */}
            <div style={{ marginBottom: '28px' }}>
              {[
                { icon: Lock, text: 'Your information is encrypted and securely processed.' },
                { icon: User, text: 'Used only for identity, security and regulatory purposes.' },
                { icon: Shield, text: 'Never visible to other NearU members.' }
              ].map((item, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px',
                    background: 'rgba(215, 181, 109, 0.05)',
                    borderRadius: '10px',
                    marginBottom: '8px'
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'rgba(215, 181, 109, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <item.icon size={16} color="#D7B56D" />
                  </div>
                  <p style={{
                    color: '#E6D7C3',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Legal Links */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <a href="/legal/privacy" style={{ color: '#A57C33', fontSize: '12px', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
              <a href="/legal/identity" style={{ color: '#A57C33', fontSize: '12px', textDecoration: 'underline' }}>
                Identity Notice
              </a>
              <a href="/legal/cardholder" style={{ color: '#A57C33', fontSize: '12px', textDecoration: 'underline' }}>
                Card Terms
              </a>
            </div>

            <button
              onClick={handleContinue}
              data-testid="verification-continue-btn"
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#2C1B13',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(215, 181, 109, 0.3)'
              }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Consent Step */}
        {step === 'consent' && (
          <div style={{ padding: '32px 24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                border: '2px solid rgba(215, 181, 109, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <FileText size={28} color="#D7B56D" />
              </div>
              
              <h2 style={{
                fontSize: '20px',
                fontFamily: 'Cinzel, serif',
                color: '#D7B56D',
                marginBottom: '8px'
              }}>
                Review & Accept
              </h2>
              
              <p style={{
                color: '#8A7355',
                fontSize: '13px'
              }}>
                Please review and accept the required agreements.
              </p>
            </div>

            {/* Consent Checkboxes */}
            <div style={{ marginBottom: '24px' }}>
              {[
                { key: 'privacy_policy', label: 'Privacy Policy', desc: 'How we collect and use your information' },
                { key: 'identity_notice', label: 'Identity Verification Notice', desc: 'Information required for verification' },
                { key: 'cardholder_agreement', label: 'Cardholder Agreement', desc: 'Terms for using your NearU Card' }
              ].map((item) => (
                <label
                  key={item.key}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '14px',
                    background: consents[item.key] ? 'rgba(76, 175, 80, 0.1)' : 'rgba(230, 215, 195, 0.03)',
                    border: `1px solid ${consents[item.key] ? 'rgba(76, 175, 80, 0.3)' : 'rgba(165, 124, 51, 0.2)'}`,
                    borderRadius: '12px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={consents[item.key]}
                    onChange={(e) => setConsents({ ...consents, [item.key]: e.target.checked })}
                    style={{
                      width: '20px',
                      height: '20px',
                      accentColor: '#D7B56D',
                      marginTop: '2px'
                    }}
                  />
                  <div>
                    <div style={{ color: '#E6D7C3', fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                      {item.label}
                    </div>
                    <div style={{ color: '#8A7355', fontSize: '12px' }}>
                      {item.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {error && (
              <div style={{
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                borderRadius: '10px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <AlertCircle size={18} color="#F44336" />
                <span style={{ color: '#F44336', fontSize: '13px' }}>{error}</span>
              </div>
            )}

            {/* Legal Notice */}
            <p style={{
              color: '#8A7355',
              fontSize: '10px',
              textAlign: 'center',
              marginBottom: '20px',
              fontStyle: 'italic'
            }}>
              [FINAL PRIVACY LANGUAGE SUBJECT TO LEGAL REVIEW]
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setStep('intro')}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'transparent',
                  border: '1px solid rgba(165, 124, 51, 0.4)',
                  borderRadius: '12px',
                  color: '#A57C33',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
              <button
                onClick={handleStartVerification}
                disabled={!allConsentsGiven || loading}
                data-testid="start-verification-btn"
                style={{
                  flex: 2,
                  padding: '14px',
                  background: allConsentsGiven 
                    ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
                    : 'rgba(138, 115, 85, 0.3)',
                  border: 'none',
                  borderRadius: '12px',
                  color: allConsentsGiven ? '#2C1B13' : '#8A7355',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: allConsentsGiven ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                Verify Identity
              </button>
            </div>
          </div>
        )}

        {/* Verifying Step */}
        {step === 'verifying' && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(215, 181, 109, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px',
              animation: 'pulse 2s infinite'
            }}>
              <Loader2 size={40} color="#D7B56D" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
            
            <h2 style={{
              fontSize: '22px',
              fontFamily: 'Cinzel, serif',
              color: '#D7B56D',
              marginBottom: '12px'
            }}>
              Verifying Your Identity
            </h2>
            
            <p style={{
              color: '#A57C33',
              fontSize: '14px',
              lineHeight: '1.6',
              maxWidth: '280px',
              margin: '0 auto'
            }}>
              We're verifying your information. This usually takes just a few moments.
            </p>

            <div style={{
              marginTop: '32px',
              padding: '12px',
              background: 'rgba(215, 181, 109, 0.05)',
              borderRadius: '10px'
            }}>
              <p style={{ color: '#8A7355', fontSize: '11px', margin: 0 }}>
                🔒 Your information is encrypted and secure
              </p>
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(76, 175, 80, 0.15)',
              border: '2px solid rgba(76, 175, 80, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px'
            }}>
              <CheckCircle size={44} color="#4CAF50" />
            </div>
            
            <h2 style={{
              fontSize: '24px',
              fontFamily: 'Cinzel, serif',
              color: '#4CAF50',
              marginBottom: '12px'
            }}>
              You're Verified!
            </h2>
            
            <p style={{
              color: '#E6D7C3',
              fontSize: '15px',
              lineHeight: '1.6',
              marginBottom: '8px'
            }}>
              Your NearU Card is ready.
            </p>

            <p style={{
              color: '#8A7355',
              fontSize: '12px',
              fontStyle: 'italic'
            }}>
              [SANDBOX MODE - No real card issued]
            </p>
          </div>
        )}

        {/* Error Step */}
        {step === 'error' && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(244, 67, 54, 0.15)',
              border: '2px solid rgba(244, 67, 54, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px'
            }}>
              <AlertCircle size={44} color="#F44336" />
            </div>
            
            <h2 style={{
              fontSize: '22px',
              fontFamily: 'Cinzel, serif',
              color: '#F44336',
              marginBottom: '12px'
            }}>
              Verification Issue
            </h2>
            
            <p style={{
              color: '#A57C33',
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {error || 'We encountered an issue verifying your identity. Please try again or contact support.'}
            </p>

            <button
              onClick={() => setStep('consent')}
              style={{
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#2C1B13',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdentityVerificationModal;
